import React, { useEffect, useState } from "react";
import { Crown, Mail, CalendarCheck, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Three-card "what next" chooser Sasha presents after a complete itinerary.
 * Triggered by the [[OFFER_ITINERARY]] tag in the AI reply.
 *
 * Options:
 *   - membership: redirect to HubSpot membership payment link
 *   - email:      redirect to HubSpot $25 itinerary-fee link
 *   - concierge:  redirect to HubSpot meetings link
 */
export default function ItineraryOfferCards({ sessionId, tripRef, budget, userInfo }) {
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(null); // 'membership' | 'email' | 'concierge'

  useEffect(() => {
    api
      .get("/itinerary-offer/links")
      .then((r) => setConfig(r.data))
      .catch(() => {});
  }, []);

  const claim = async (option, tier_id) => {
    setBusy(option);
    try {
      const r = await api.post("/itinerary-offer/claim", {
        session_id: sessionId,
        trip_ref: tripRef || null,
        option,
        tier_id: tier_id || null,
        email: userInfo?.email || null,
        name: userInfo?.name || null,
        phone: userInfo?.phone || null,
        budget_snapshot: budget || null,
      });
      const url = r.data.redirect_url;
      if (!url) {
        toast.message(
          "We've recorded your choice — payment link arriving shortly. Your concierge will reach out.",
        );
        return;
      }
      // Persist a return-flag so when HubSpot redirects back to /chat, Sasha knows what to say.
      try {
        window.localStorage.setItem(
          "jante_offer_return",
          JSON.stringify({
            session_id: sessionId,
            trip_ref: tripRef || null,
            option,
            tier_id: tier_id || null,
            at: new Date().toISOString(),
          }),
        );
      } catch {
        /* storage unavailable */
      }
      window.location.href = url;
    } catch (e) {
      console.error("offer claim failed", e);
      toast.error("Could not record your choice — please try again.");
    } finally {
      setBusy(null);
    }
  };

  const fee = config?.fee_usd ?? 25;

  return (
    <div className="lux-card mt-4 max-w-3xl border-t-4 border-[var(--gold)]" data-testid="offer-cards">
      <Sparkles size={20} strokeWidth={1.25} className="text-[var(--gold)]" />
      <div className="overline mt-3">A few ways to take this further</div>
      <h3 className="font-display text-3xl text-[var(--emerald)] mt-2">
        How would you like to receive your trip?
      </h3>

      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        {/* Membership */}
        <button
          type="button"
          onClick={() => claim("membership", "joiner")}
          disabled={busy !== null}
          className="lux-card text-left transition-all hover:-translate-y-0.5 ring-1 ring-[var(--gold)]"
          data-testid="offer-membership"
        >
          <Crown size={20} strokeWidth={1.25} className="text-[var(--gold)]" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--brand-red)] mt-3">
            Best value
          </div>
          <div className="font-display text-2xl text-[var(--emerald)] mt-1">
            Join Club Janté
          </div>
          <ul className="text-xs text-[var(--ink-2)] mt-3 space-y-1.5">
            <li>· Unlimited trip changes & cancellations</li>
            <li>· Member-only sales & private rates</li>
            <li>· Dedicated concierge included</li>
            <li>· Priority booking & insider access</li>
            <li>· Saves more than DIY booking over a year</li>
          </ul>
          <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--emerald)]">
            See your tier <ArrowRight size={11} strokeWidth={1.5} />
          </div>
        </button>

        {/* Email itinerary */}
        <button
          type="button"
          onClick={() => claim("email")}
          disabled={busy !== null}
          className="lux-card text-left transition-all hover:-translate-y-0.5"
          data-testid="offer-email"
        >
          <Mail size={20} strokeWidth={1.25} className="text-[var(--gold)]" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--brand-red)] mt-3">
            ${fee} · One-time
          </div>
          <div className="font-display text-2xl text-[var(--emerald)] mt-1">
            Email me the formatted itinerary
          </div>
          <p className="text-xs text-[var(--ink-2)] mt-3">
            A beautifully laid-out, ready-to-print itinerary delivered to your inbox — flights,
            ground transport, activities, dining, the full plan.
          </p>
          <p className="text-[11px] text-[var(--brand-red)] mt-3 italic leading-relaxed">
            If you book more than 4 trips a year, you might be better off as a Club Janté Access client.
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--emerald)]">
            {busy === "email" ? "Redirecting…" : `Pay $${fee} & receive`} <ArrowRight size={11} strokeWidth={1.5} />
          </div>
        </button>

        {/* Crown concierge */}
        <button
          type="button"
          onClick={() => claim("concierge")}
          disabled={busy !== null}
          className="lux-card text-left transition-all hover:-translate-y-0.5"
          data-testid="offer-concierge"
        >
          <CalendarCheck size={20} strokeWidth={1.25} className="text-[var(--gold)]" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--brand-red)] mt-3">
            All the bells &amp; whistles
          </div>
          <div className="font-display text-2xl text-[var(--emerald)] mt-1">
            Janté Crown — make an appointment
          </div>
          <p className="text-xs text-[var(--ink-2)] mt-3">
            The Janté Crown membership gets you concierge services from door to door — like
            travel insurance before the trip is ruined by an experience.
            <span className="block text-[10px] mt-1 opacity-70">
              (Not a replacement for recommended travel insurance.)
            </span>
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--emerald)]">
            {busy === "concierge" ? "Redirecting…" : "Make an appointment"} <ArrowRight size={11} strokeWidth={1.5} />
          </div>
        </button>
      </div>

      <p className="text-[11px] text-[var(--muted)] mt-5">
        After payment, you'll be returned to Sasha to finalise the details.
      </p>
    </div>
  );
}
